import { useTranslation } from "react-i18next";
import {
  useGetAccessGroupById,
  useGetAllAccessGroups,
  useGetAllBuildingsById,
  useGetAllFRDevicesDD,
  useGetAllLocationsDropdown,
} from "../../../hooks/common/query";
import { useCallback, useEffect, useMemo, useState } from "react";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import AccessGroupsTable from "../../../components/table/AccessGroupsTable";
import { Button } from "../../../components/button";
import TextInput from "../../../components/inputs/TextInput";
import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { AccessGroupsSchema } from "../../../schemas/accessGroupSchema";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { mapToOptions } from "../../../lib/functions";
import SelectInput from "../../../components/inputs/SelectInput";
import { PAGE_SIZE, SelectInputOption } from "../../../constants/common";
import CheckboxInput from "../../../components/inputs/CheckboxInput";
import toast, { Toaster } from "react-hot-toast";
import {
  useAddUpdateAccessGroup,
  useIsAccessGroupExists,
} from "../../../hooks/common/mutation";

interface FormData {
  accessGroupEn: string;
  accessGroupAr: string;
  location: string;
  building: string;
}

const initialData = {
  accessGroupEn: "",
  accessGroupAr: "",
  location: "",
  building: "",
};

const AccessGroups = () => {
  const { t, i18n } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [allAccessGroups, setAllAccessGroups] = useState([]);

  const [isLocationDefaultGroup, setIsLocationDefaultGroup] =
    useState<boolean>(false);

  const [selectedAccessGroupId, setSelectedAccessGroupId] = useState(0);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useFormWithSchema(AccessGroupsSchema);

  const {
    mutateAsync: AddUpdateAccessGroup,
    isPending: isAddUpdateAccessGroupLoading,
  } = useAddUpdateAccessGroup();

  const [selectedDevicesOptions, setSelectedDevicesOptions] = useState<
    SelectInputOption[]
  >([]);

  const { data: selectedAccessGroup } = useGetAccessGroupById({
    encryptedId: selectedAccessGroupId ? selectedAccessGroupId.toString() : "",
  });

  const { data: accessGroups } = useGetAllAccessGroups({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });
  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allFRDevices } = useGetAllFRDevicesDD();

  useEffect(() => {
    if (selectedAccessGroup) {
      setValue("accessGroupEn", selectedAccessGroup.s_AccessGroupName_En);
      setValue("accessGroupAr", selectedAccessGroup.s_AccessGroupName_Ar);
      setValue("location", selectedAccessGroup.n_LocationID.toString());
      setValue("building", selectedAccessGroup.n_BuildingID.toString());
      setIsLocationDefaultGroup(selectedAccessGroup.n_IsAreaDefaultGroup === 1);

      if (selectedAccessGroup?.frDevices?.length && allFRDevices?.length) {
        const matchedDevices: SelectInputOption[] =
          selectedAccessGroup.frDevices
            .map((frDevice) => {
              const match = allFRDevices.find(
                (device: { n_FRDeviceID: number; n_DeviceID: number }) =>
                  device.n_FRDeviceID === frDevice.n_FRDeviceID ||
                  device.n_DeviceID === frDevice.n_FRDeviceID
              );
              if (match) {
                return {
                  value: match.n_FRDeviceID || match.n_DeviceID,
                  label:
                    i18n.language === "en"
                      ? match.s_DeviceName_En
                      : match.s_DeviceName_Ar,
                  color: "#403f68",
                };
              }
              return null;
            })
            .filter(Boolean) as SelectInputOption[];

        setSelectedDevicesOptions(matchedDevices);
      }
    }
  }, [setValue, selectedAccessGroup, allFRDevices, i18n.language]);

  useEffect(() => {
    if (accessGroups) {
      setAllAccessGroups(accessGroups.data || []);
      setPages(accessGroups.pagination?.pages || 1);
      setCount(accessGroups.pagination?.count || 0);
    }
  }, [setPages, accessGroups]);

  const frDevices = useMemo(
    () =>
      allFRDevices?.map(
        (item: {
          n_DeviceID: string;
          s_DeviceName_En: string;
          s_DeviceName_Ar: string;
        }) => ({
          value: item.n_DeviceID,
          label:
            i18n.language === "en"
              ? item.s_DeviceName_En
              : item.s_DeviceName_Ar,
          color: "#403f68",
        })
      ),
    [allFRDevices, i18n.language]
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const selectedLocationId = watch("location");

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId
      ? selectedLocationId.toString()
      : selectedAccessGroup?.n_LocationID
      ? selectedAccessGroup?.n_LocationID.toString()
      : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  const { mutateAsync: IsAccessGroupExists } = useIsAccessGroupExists();

  const onClear = useCallback(() => {
    reset(initialData);
    setIsLocationDefaultGroup(false);
    setSelectedDevicesOptions([]);
  }, [reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (selectedDevicesOptions.length === 0) {
        toast.error(t("kindlySelectDevice"));
        return;
      }

      try {
        const isExistBody = {
          encryptedId: selectedAccessGroupId
            ? selectedAccessGroupId?.toString()
            : "",
          s_AccessGroupName_En: data.accessGroupEn,
          s_AccessGroupName_Ar: data.accessGroupAr,
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
        };
        const { isExists } = await IsAccessGroupExists(isExistBody);

        if (isExists) {
          toast.error(t("accessGroupExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error checking if access group exists", error);
        return;
      }

      try {
        const body = {
          n_AccessGroupID: selectedAccessGroupId,
          s_AccessGroupName_En: data.accessGroupEn,
          s_AccessGroupName_Ar: data.accessGroupAr,
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
          n_IsAreaDefaultGroup: isLocationDefaultGroup ? 1 : 0,
          frDevices: selectedDevicesOptions?.map((item) => ({
            n_FRDeviceID: Number(item.value),
          })),
        };

        if (selectedAccessGroupId === 0) {
          await AddUpdateAccessGroup(body);
        } else {
          const updateBody = {
            ...body,
            n_AccessGroupID: selectedAccessGroupId,
          };

          await AddUpdateAccessGroup(updateBody);
        }

        setSelectedAccessGroupId(0);

        if (selectedAccessGroupId === 0) {
          toast.success(t("accessGroupAdded"));
        } else {
          toast.success(t("accessGroupUpdated"));
        }

        onClear();
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error adding/updating access group", error);
      }
    },
    [
      AddUpdateAccessGroup,
      IsAccessGroupExists,
      isLocationDefaultGroup,
      onClear,
      selectedAccessGroupId,
      selectedDevicesOptions,
      t,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("accessGroups")}
          </h1>
        </BackButtonWrapper>

        <div className="flex flex-col gap-2">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4 md:gap-8"
          >
            <div className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col gap-3 md:gap-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                <Controller
                  name="accessGroupEn"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("accessGroupEn")}
                      onChange={onChange}
                      value={value || ""}
                      error={errors?.[name]?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="accessGroupAr"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("accessGroupAr")}
                      onChange={onChange}
                      value={value || ""}
                      error={errors?.[name]?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="location"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <ComboboxInput
                      options={locations}
                      value={
                        locations?.find((item) => item.value === Number(value))
                          ?.labelEn || ""
                      }
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      label={t("location")}
                      required
                    />
                  )}
                />

                <Controller
                  name="building"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <ComboboxInput
                      options={buildings}
                      value={
                        buildings?.find((item) => item.value === Number(value))
                          ?.labelEn || ""
                      }
                      required
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      label={t("building")}
                    />
                  )}
                />
              </div>

              <div>
                <SelectInput
                  label={t("devices")}
                  options={frDevices}
                  selectedOptions={selectedDevicesOptions}
                  setSelectedOptions={setSelectedDevicesOptions}
                />
              </div>

              <div className="flex items-center gap-2">
                <CheckboxInput
                  key="location-default-group"
                  id="location-default-group"
                  label={t("locationDefaultGroup")}
                  checked={isLocationDefaultGroup}
                  setChecked={() => setIsLocationDefaultGroup((prev) => !prev)}
                />
              </div>

              <div className="flex items-center gap-2">
                <Button isLoading={isAddUpdateAccessGroupLoading}>
                  {t("save")}
                </Button>
                <Button
                  type="button"
                  onClick={onClear}
                  className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
                >
                  {t("clear")}
                </Button>
              </div>
            </div>
            <AccessGroupsTable
              accessGroups={allAccessGroups}
              setSelectedAccessGroupId={setSelectedAccessGroupId}
              page={page}
              pages={pages}
              setPage={setPage}
              search={search}
              setSearch={setSearch}
              count={count}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default AccessGroups;
